/**
 * Interfaces related to the Action service
 * 
 * ### Action lifecycle :
 * - createAction()
 * - processOperations()
 * - onAfterProcessOperation
 * - addToUndoList() // client side only
 * - addToPersistQueue()
 * - processPersistQueue();
 * 
 * ### Persist lifecycle (triggered by processPersistQueue)
 * - onBeforePersist
 * - sendActionsToPersistNow()
 * - onAfterPersist // called even if there is an error
 * - updateLocalCache()
 * - onAfterUpdateLocalCache
 * - [if params.waitingForReply] waitForReplyPromise.resolve or reject
 * - [if !params.waitingForReply && result.error] onPersistError
 * - processPersistQueue();
 * 
 * @packageDocumentation
 * @module ActionService
 */
import { CommonEventHandler } from "@cervval.js/common/src/tools/event.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { Action, ActionInfo, ActionParams, ActionPersistResult } from "../action.model";

/**
 * Callback passed to encapsulateInAction functions
 */
export type ProcessOperations<ElementTypes = unknown, AppLogInfos = unknown, Context = unknown, Result = unknown> =
    (action: Action<ElementTypes, AppLogInfos, Context>) => Promise<Result> | Result;

/**
 * This function is used in components that want to initiate an action without knowing the current application context
 */
export type EncapsulateInActionWithoutParams<ElementTypes = unknown, AppLogInfos = unknown, Context = unknown, Result = unknown> =
    (processOperations: ProcessOperations<ElementTypes, AppLogInfos, Context, Result>) => Promise<Result>;

export const ACTION_SERVICE = "ActionService";
/**
 * Actions manage the creation, deletion and modification of objects that are persisted (usually in a database).
 * Operations are processed immediately but sent to 'server' for persistence asynchronously once all operations are processed.
 */
export interface ActionService<ElementTypes, Context> {

    /** 
     * Event emitted when undo / redo stacks are cleared
     */
    onClear: CommonEventHandler<void>;

    /** 
     * Event emitted once action is executed or undo/redo is completed (may not be persisted)
     * As an example, applications listen to this event to update undo and redo buttons states
     */
    onAfterActionProcessed: CommonEventHandler<ActionInfo<ElementTypes, Context>>;

    /**
     * Event emitted after all actions have been processed server side and local cache have been updated client side
     */
    afterUpdateLocalCache: CommonEventHandler<{ actions: Action<ElementTypes, unknown, anyReallyAny>[], results: ActionPersistResult[] }>;

    /**
     * Creates an action and once processOperations is finished, sends the operations to be persisted
     */
    encapsulateInAction<AppLogInfos, Result>(
        processOperations: ProcessOperations<ElementTypes, AppLogInfos, Context, Result>,
        params: ActionParams<ElementTypes, AppLogInfos, Context>): Promise<Result>;

    /**
     * Submit a detached action to server: updates the global cache service 
     * (changes the action cacheManager to the global cache service and redo all operations) 
     * then send the action to server as usual.
     * (@see {@link submitDetachedActionWithResult} for a version with a result parameter available in callbacks)
     * @param action detachedAction
     * @param tableDefinitionsMap used to clear association before re-adding to array when updating global cache
     * @param options
     * @param options.result result is provided as a parameter in action onAfterProcessOperation callback and returned within promise chain
     * @param options.doNotRemoveInvalidOperations if not set to true, invalid operations (update of deleted item...) will be remove from action before submit
     */
    submitDetachedAction<AppLogInfos, Result>(action: Action<ElementTypes, AppLogInfos, Context>, options?: { result?: Result, doNotRemoveInvalidOperations?: boolean }): Promise<Result>;

    /**
     * @returns true if undo/redo is enabled and there is at least one action to undo
     */
    canUndo(): boolean;

    /**
     * @returns true if undo/redo is enabled and there is at least one action to redo
     */
    canRedo(): boolean;

    /**
     * Undo one action if there is an action to undo (note that persist will be done asynchronously)
     */
    undo(): void;

    /**
     * Redo one action if there is an action to redo (note that persist will be done asynchronously)
     */
    redo(): void;

    /**
     * clear the undo/redo stacks
     */
    clear(): void;

}